import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Button } from 'components/ui/button';
import { Icon, type IconName } from 'components/ui/icon';
import { GTW_STATUS, GTW_TRANSACTION_TYPE } from 'utils/constants';
import { TransactionActionsMenuProps } from '../types';

interface ActionMenuItemProps {
  icon: IconName;
  label: string;
  description: string;
  onClick: () => void;
  testId: string;
}

function ActionMenuItem({ icon, label, description, onClick, testId }: ActionMenuItemProps) {
  return (
    <DropdownMenuItem
      data-testid={testId}
      className="cursor-pointer flex-col items-start p-2 focus:bg-gray-blue-5"
      onClick={onClick}
    >
      <div className="flex items-center text-gray-blue-80">
        <span className="flex items-center text-sm leading-5 text-gray-blue-80">
          <Icon name={icon} size={16} className="mr-1.5 text-gray-blue-60 flex" />
          {label}
        </span>
      </div>
      <span className="mt-1 whitespace-normal text-left ml-5.5 text-xs leading-4 text-gray-blue-60">
        {description}
      </span>
    </DropdownMenuItem>
  );
}

export function TransactionActionsMenu({
  row,
  onVerifySale,
  onRecordSale,
  onRefund,
  canRecordSale,
}: TransactionActionsMenuProps) {
  const showVerifySale = row.gateway_status !== GTW_STATUS.SUCCESS;
  const showRefund =
    row.method === 'ACH' &&
    row.gateway_status === GTW_STATUS.SUCCESS &&
    row.type !== GTW_TRANSACTION_TYPE.REFUND;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 p-0 hover:bg-gray-blue-5"
            aria-label="Transaction actions"
          >
            <Icon name="dots" size={18} className="text-gray-blue-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 p-2">
          {showVerifySale && (
            <ActionMenuItem
              testId="verify-sale"
              icon="refresh"
              label="Verify Sale"
              description="Check status of the transaction with payment device to close the sale."
              onClick={() => onVerifySale(row)}
            />
          )}
          {canRecordSale && (
            <ActionMenuItem
              testId="update-status"
              icon="pencil"
              label="Record Sale"
              description="Forcefully record this sale as successful and mark as paid."
              onClick={() => onRecordSale(row)}
            />
          )}
          {showRefund && (
            <ActionMenuItem
              testId="refund-payment"
              icon="rotate-ccw"
              label="Refund"
              description="Process a full refund for this payment."
              onClick={() => onRefund(row)}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
